import { TFunction } from 'i18next';
import {
  ActionType,
  SendMessageActionFormData,
  SimplifiedWorkflowActionStepFormData,
  SimplifiedWorkflowStepFormData,
  SimplifiedWorkflowTriggerStepFormData,
  StepType,
} from '../data/models/simplified-workflow.model';
import { isNil, upperFirst } from 'lodash';
import describeWorkflowUtils from './describeWorkflowUtils';
import simplifiedWorkflowUtils from './simplifiedWorkflowUtils';

class DescribeSimplifiedWorkflowUtils {
  public getStepDescription(
    step: SimplifiedWorkflowStepFormData,
    translateFunction: TFunction,
  ): string[] {
    switch (step.type) {
      case StepType.Trigger:
        return this.getStepDescriptionForTrigger(step, translateFunction);
      case StepType.Action:
        return this.getStepDescriptionForAction(step, translateFunction);
    }
  }

  private getStepDescriptionForTrigger(
    step: SimplifiedWorkflowTriggerStepFormData,
    translateFunction: TFunction,
  ): string[] {
    if (!step.event || !step.wait) {
      return [];
    }
    const { waitAmount, waitAmountUnit, waitTimePeriod } = step.wait;
    if (!isNil(waitAmount) && waitAmountUnit && waitTimePeriod) {
      const waitAmountInt = parseInt(waitAmount);
      const unit = describeWorkflowUtils.getFormattedWaitAmountUnit(
        waitAmount,
        waitAmountUnit,
      );
      if (waitAmountInt === 0) {
        return [
          translateFunction('workflows.triggerDesc', {
            event: step.event,
          }),
        ];
      } else {
        return [
          `${waitAmount} ${unit} ${upperFirst(waitTimePeriod)} ${step.event}`,
        ];
      }
    }
    return [];
  }

  private getStepDescriptionForAction(
    step: SimplifiedWorkflowActionStepFormData,
    translateFunction: TFunction,
  ): string[] {
    if (!step.actionType) {
      return [];
    }
    switch (step.actionType) {
      case ActionType.SendMessage:
        return this.getStepDescriptionForSendMessageAction(
          step,
          translateFunction,
        );
      default:
        return [];
    }
  }

  private getStepDescriptionForSendMessageAction(
    step: SendMessageActionFormData,
    translateFunction: TFunction,
  ): string[] {
    const { messageType, recipient } = step;
    if (messageType && recipient) {
      return [
        translateFunction('workflows.sendMessageDesc', {
          messageType: translateFunction(
            `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageType(messageType)}`,
          ),
          recipient: translateFunction(
            `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageRecipient(recipient)}`,
          ),
        }),
      ];
    }
    return [];
  }
}

const describeSimplifiedWorkflowUtils = new DescribeSimplifiedWorkflowUtils();

export default describeSimplifiedWorkflowUtils;
